import { useState, useEffect } from 'react';
import { X, Truck, MapPin, Save, AlertCircle, Globe } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const AddVehicleModal = ({ isOpen, onClose, onVehicleAdded }) => {
  const { getThemeClasses, theme } = useTheme();
  const { getAuthHeaders } = useAuth();
  const themeClasses = getThemeClasses();

  // Form state
  const [formData, setFormData] = useState({
    brand: '',
    type: '',
    model: '',
    year: new Date().getFullYear(),
    mileage: '',
    regionId: '',
    baseId: '',
  });

  // UI state
  const [countries, setCountries] = useState([]);
  const [bases, setBases] = useState([]);
  const [filteredBases, setFilteredBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Vehicle type options
  const vehicleTypes = ['MRAP', 'HMMWV', 'JLTV', 'M113'];
  const brandOptions = ['Prime Mover', 'Armament Carrier'];

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCountriesAndBases();
      // Reset form when modal opens
      setFormData({
        brand: '',
        type: '',
        model: '',
        year: new Date().getFullYear(),
        mileage: '',
        regionId: '',
        baseId: '',
      });
      setError('');
    }
  }, [isOpen]);

  // Filter bases when region changes
  useEffect(() => {
    if (formData.regionId) {
      const filtered = bases.filter(base => base.countryId === formData.regionId);
      setFilteredBases(filtered);
      // Reset base selection when region changes
      if (formData.baseId) {
        const baseStillValid = filtered.some(base => base.id === formData.baseId);
        if (!baseStillValid) {
          setFormData(prev => ({ ...prev, baseId: '' }));
        }
      }
    } else {
      setFilteredBases([]);
      setFormData(prev => ({ ...prev, baseId: '' }));
    }
  }, [formData.regionId, bases]);

  const fetchCountriesAndBases = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      
      // Fetch bases
      const basesResponse = await fetch(`${apiUrl}/api/fleet/bases`, { headers: getAuthHeaders() });
      if (!basesResponse.ok) {
        throw new Error('Failed to fetch bases');
      }
      const basesData = await basesResponse.json();
      setBases(basesData);

      // Extract unique countries from bases data
      const uniqueCountries = basesData.reduce((acc, base) => {
        if (!acc.find(country => country.id === base.countryId)) {
          acc.push({
            id: base.countryId,
            name: base.countryName
          });
        }
        return acc;
      }, []);
      
      setCountries(uniqueCountries);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load regions and bases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    const { brand, type, model, mileage, regionId, baseId } = formData;
    
    if (!brand) return 'Please select a brand';
    if (!type) return 'Please select a vehicle type';
    if (!model.trim()) return 'Please enter a model';
    if (!mileage || mileage < 0) return 'Please enter a valid mileage';
    if (!regionId) return 'Please select a region';
    if (!baseId) return 'Please select a base';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      const response = await fetch(`${apiUrl}/api/fleet/vehicle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand: formData.brand,
          type: formData.type,
          model: formData.model.trim(),
          mileage: parseInt(formData.mileage),
          baseId: formData.baseId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add vehicle');
      }

      const result = await response.json();
      
      // Call the callback to update the parent component
      if (onVehicleAdded) {
        onVehicleAdded(result.vehicle);
      }

      // Close modal and show success
      onClose();
      
      // You could add a toast notification here
      console.log('✅ Vehicle added successfully:', result.vehicle);

    } catch (error) {
      console.error('❌ Error adding vehicle:', error);
      setError(error.message || 'Failed to add vehicle. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedBaseInfo = () => {
    const selectedBase = filteredBases.find(base => base.id === formData.baseId);
    return selectedBase ? `${selectedBase.location}, ${selectedBase.countryName}` : '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border ${themeClasses.border} rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${themeClasses.border}`}>
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
              <Truck size={20} className="text-white" />
            </div>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary}`}>Add New Vehicle</h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.textMuted} hover:${themeClasses.textPrimary} hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Brand Selection */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              Brand *
            </label>
            <select
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
              required
            >
              <option value="">Select Brand</option>
              {brandOptions.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          {/* Type Selection */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
              required
            >
              <option value="">Select Type</option>
              {vehicleTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Model Input */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              Model *
            </label>
            <input
              type="text"
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              placeholder="Enter vehicle model"
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
              required
            />
          </div>

          {/* Year Input */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              Year *
            </label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => handleInputChange('year', parseInt(e.target.value) || new Date().getFullYear())}
              placeholder="Enter vehicle year"
              min="1990"
              max={new Date().getFullYear() + 1}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
              required
            />
          </div>

          {/* Mileage Input */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              Mileage *
            </label>
            <input
              type="number"
              value={formData.mileage}
              onChange={(e) => handleInputChange('mileage', e.target.value)}
              placeholder="Enter current mileage"
              min="0"
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
              required
            />
          </div>

          {/* Region Selection */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              <Globe className="inline mr-1" size={14} />
              Region *
            </label>
            <select
              value={formData.regionId}
              onChange={(e) => handleInputChange('regionId', e.target.value)}
              disabled={loading}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50`}
              required
            >
              <option value="">
                {loading ? 'Loading regions...' : 'Select Region'}
              </option>
              {countries.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          {/* Base Selection */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
              <MapPin className="inline mr-1" size={14} />
              Base *
            </label>
            <select
              value={formData.baseId}
              onChange={(e) => handleInputChange('baseId', e.target.value)}
              disabled={loading || !formData.regionId}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50`}
              required
            >
              <option value="">
                {loading ? 'Loading bases...' : 
                 !formData.regionId ? 'Select region first' : 
                 filteredBases.length === 0 ? 'No bases available' : 'Select Base'}
              </option>
              {filteredBases.map(base => (
                <option key={base.id} value={base.id}>
                  {base.name} - {base.location}
                </option>
              ))}
            </select>
          </div>

          {/* Location Display */}
          {formData.baseId && (
            <div>
              <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                Location
              </label>
              <div className={`w-full px-4 py-2 ${themeClasses.secondary} border ${themeClasses.border} rounded-lg ${themeClasses.textMuted} text-sm`}>
                {getSelectedBaseInfo()}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Add Vehicle
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVehicleModal;
